import { z } from 'zod';

/**
 * RESIDENT PUBLIC-INFORMATION release + dispatch (#450, the #420b resident half,
 * migration 0133).
 *
 * A MUNICIPAL Emergency Information Officer (EIO) publishes a PUBLIC-INFORMATION RELEASE
 * to the municipality's OWN opted-in resident list over its OWN channel — Ontario IMS
 * §7.10 / Appendix A #62 ("the public information management section … develops and shares
 * messages with the public directly"), the EIO is App A #23. The municipality ISSUES its
 * own release.
 *
 * IT IS NOT ALERT READY. The national wireless broadcast (Alert Ready / En Alerte) is
 * operated by the PEOC "on behalf of communities" (PERP §5.5.1) and a municipality only
 * REQUESTS it via the existing request_public_alert → PEOC path (#497). This channel never
 * routes through, imitates, or is labelled as Alert Ready.
 *
 * RESIDENT-BODY DISCIPLINE (mirrors public-alert-request.ts's no-raw-CAP guard): nothing
 * operator-facing reaches a resident. The body is plain text — evidence [n] citation
 * markers are STRIPPED (toResidentReleaseBody) and any hazard/status enum is rendered ONLY
 * through the surface's doctrine-safe HAZARD_LABEL map (never the raw value).
 */

const UUID = z.string().uuid();

/** Max resident-release body length. Generous for a paragraph release, bounded so a runaway
 *  body can't drain the shared SMS budget (≈ ceil(1000/70) = 15 UCS-2 segments per recipient). */
export const PUBLIC_INFO_BODY_MAX_LEN = 1000;

/**
 * POST /api/public-info/publish request. `community_id` targets the opted-in resident list
 * (each resident is bound to a community); `incident_id` is the optional originating
 * incident context; `body` is the composed, plain-text resident release (markers already
 * stripped client-side — the server also strips defensively).
 */
export const PublicInfoPublishSchema = z.object({
  community_id: UUID,
  incident_id: UUID.optional(),
  body: z.string().trim().min(1).max(PUBLIC_INFO_BODY_MAX_LEN),
  session_id: z.string().min(1).max(128),
});
export type PublicInfoPublishRequest = z.infer<typeof PublicInfoPublishSchema>;

/** POST /api/public-info/publish response. */
export interface PublicInfoPublishResult {
  /** Correlates the fan-out — every delivery_log row for this publish shares it. */
  release_id: string;
  community_id: string;
  community_name: string;
  incident_id: string | null;
  /** Opted-in residents targeted (excludes opted_out). */
  recipient_count: number;
  /** Residents the console/carrier accepted (== recipient_count on the console path). */
  delivered_count: number;
  /** Residents skipped because the shared SMS budget cap was reached mid-fan-out. */
  capped_count: number;
}

/**
 * Transform an operator-facing Public Information Draft into a RESIDENT-safe body: remove the
 * evidence [n] citation markers (they exist only so an OPERATOR can verify the draft against
 * its sources — a resident must never see "[1]") and tidy the whitespace the removal leaves.
 * Pure + shared so the compose surface and the marker-strip lock test agree byte-for-byte.
 */
export function toResidentReleaseBody(draft: string): string {
  return draft
    .replace(/\[\d+\]/g, '') // drop [n] citation markers
    .replace(/[ \t]+/g, ' ') // collapse the runs of spaces the removal leaves
    .replace(/ +([.,;:!?])/g, '$1') // no space before punctuation (an inline-marker artifact)
    .replace(/ +\n/g, '\n') // trailing spaces before a newline
    .replace(/\n{3,}/g, '\n\n') // no more than one blank line
    .trim();
}
